import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { subscribeToEvent, socket, requestAlerts } from "core/api/socket";
import { getAlertsService } from "modules/noc/services/alertService";
import { alertStats, filterAlerts, socketAlertItems, sortAlerts } from "../model/alertsModel";

const sounds = new Map();
function playPrioritySound(priority) {
  const kind = priority >= 4 ? "critical" : priority >= 3 ? "warning" : "info";
  if (!sounds.has(kind)) {
    const files = {
      info: "mixkit-signal-alert-771.wav",
      warning: "mixkit-vintage-warning-alarm-990.wav",
      critical: "mixkit-critical-alarm-1004.wav",
    };
    const audio = new Audio(`/sounds/${files[kind]}`);
    audio.volume = kind === "critical" ? 1 : kind === "warning" ? 0.7 : 0.4;
    sounds.set(kind, audio);
  }
  const original = sounds.get(kind),
    audio = original.cloneNode();
  audio.volume = original.volume;
  audio.play()?.catch(() => {});
}
function isAlertSnapshot(value) {
  if (typeof value === "string") {
    try {
      return isAlertSnapshot(JSON.parse(value));
    } catch {
      return false;
    }
  }
  if (Array.isArray(value))
    return value.length === 0 || value.every((item) => socketAlertItems(item).length > 0);
  if (!value || typeof value !== "object") return false;
  if (
    value.success === false ||
    Number(value.code || 0) !== 0 ||
    Number(value.status || 200) >= 400
  )
    return false;
  return value.data != null && isAlertSnapshot(value.data);
}
export const useAlertas = () => {
  const [allAlerts, setAllAlerts] = useState([]);
  const [activeFilter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socketConnected, setSocketConnected] = useState(Boolean(socket?.connected));
  const [reconnecting, setReconnecting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mounted = useRef(false),
    current = useRef([]),
    pending = useRef(null),
    requestId = useRef(0);
  const lastSound = useRef(-Infinity),
    revision = useRef(0),
    liveRevisions = useRef(new Map()),
    snapshotRevision = useRef(0);
  const commit = useCallback((items) => {
    const rows = sortAlerts(items);
    current.current = rows;
    if (mounted.current) setAllAlerts(rows);
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current++;
    };
  }, []);
  const processSocketAlert = useCallback(
    (value) => {
      if (!mounted.current) return;
      const items = socketAlertItems(value);
      // Arrays are full snapshots, while a single alert is an incremental event.
      const snapshot = isAlertSnapshot(value);
      if (!snapshot && !items.length) return;
      if (snapshot) snapshotRevision.current = ++revision.current;
      for (const item of items) {
        const previous = current.current.find((alert) => alert.id === item.id);
        if (!previous && Date.now() - lastSound.current >= 1500) {
          lastSound.current = Date.now();
          try {
            playPrioritySound(item.priority);
          } catch {
            /* Audio may be blocked until the user interacts. */
          }
        }
        liveRevisions.current.set(item.id, ++revision.current);
      }
      commit(
        snapshot
          ? items
          : [...new Map([...current.current, ...items].map((item) => [item.id, item])).values()]
      );
      setLastUpdated(new Date().toISOString());
    },
    [commit]
  );
  const loadAlertsFromAPI = useCallback(async () => {
    const id = ++requestId.current,
      started = revision.current;
    setLoading(true);
    setError(null);
    if (!pending.current) {
      pending.current = getAlertsService();
      const promise = pending.current;
      promise
        .finally(() => {
          if (pending.current === promise) pending.current = null;
        })
        .catch(() => {});
    }
    try {
      const result = await pending.current;
      if (!mounted.current || id !== requestId.current) return { success: false };
      if (!result.success) throw new Error(result.error || "No se pudieron cargar las alertas");
      if (snapshotRevision.current > started) return { success: true };
      const rows = new Map(result.data.map((item) => [item.id, item]));
      for (const item of current.current)
        if ((liveRevisions.current.get(item.id) || 0) > started) rows.set(item.id, item);
      commit([...rows.values()]);
      setLastUpdated(new Date().toISOString());
      return { success: true };
    } catch (err) {
      if (mounted.current && id === requestId.current)
        setError(err.message || "Error al cargar alertas");
      return { success: false, error: err.message };
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [commit]);
  useEffect(() => {
    const off = [
      subscribeToEvent("connect", () => {
        setSocketConnected(true);
        setReconnecting(false);
        requestAlerts();
        loadAlertsFromAPI();
      }),
      subscribeToEvent("disconnect", () => setSocketConnected(false)),
      ...["zabbix_alert", "message", "reader_alert"].map((name) =>
        subscribeToEvent(name, processSocketAlert)
      ),
    ];
    const manager = socket?.io;
    const reconnectingHandler = () => setReconnecting(true);
    const reconnectEnd = () => setReconnecting(false);
    manager?.on("reconnect_attempt", reconnectingHandler);
    manager?.on("reconnect", reconnectEnd);
    manager?.on("reconnect_failed", reconnectEnd);
    socket?.connect?.();
    return () => {
      off.forEach((unsubscribe) => unsubscribe());
      manager?.off("reconnect_attempt", reconnectingHandler);
      manager?.off("reconnect", reconnectEnd);
      manager?.off("reconnect_failed", reconnectEnd);
      socket?.disconnect?.();
    };
  }, [processSocketAlert, loadAlertsFromAPI]);
  useEffect(() => {
    loadAlertsFromAPI();
    const interval = setInterval(() => {
      if (!pending.current) loadAlertsFromAPI();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadAlertsFromAPI]);
  const alerts = useMemo(
    () =>
      filterAlerts(allAlerts, {
        severity: activeFilter,
        type: typeFilter,
        search: searchTerm,
      }),
    [allAlerts, activeFilter, typeFilter, searchTerm]
  );
  const stats = useMemo(() => alertStats(allAlerts), [allAlerts]);
  const refreshData = useCallback(async () => {
    const result = await loadAlertsFromAPI();
    if (socket?.connected) requestAlerts();
    return result;
  }, [loadAlertsFromAPI]);
  return {
    alerts,
    allAlerts,
    stats,
    loading,
    isLoading: loading,
    error,
    showNoData: !loading && Boolean(error) && !allAlerts.length,
    socketConnected,
    reconnecting,
    activeFilter,
    typeFilter,
    searchTerm,
    lastUpdated,
    setFilter,
    setTypeFilter,
    setSearch,
    refreshData,
  };
};
